import { getApiError } from '../lib/apiError';
import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Eye, EyeOff, CheckCircle, XCircle, Lock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        setTokenValid(false);
        return;
      }

      try {
        await axios.get(`${API_URL}/api/auth/verify-reset-token?token=${token}`);
        setTokenValid(true);
      } catch (error) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.password || !formData.confirmPassword) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: formData.password
      });
      setSuccess(true);
      toast.success('Senha redefinida com sucesso!');
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao redefinir senha'));
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Verificando link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="reset-password-page">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/30">
              <span className="text-white font-bold text-xl">R</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 tracking-tight">RPB SISTEMAS</h1>
              <p className="text-slate-500 text-sm">INTELIGENTES</p>
            </div>
          </div>

          <Card className="border-0 shadow-none">
            {!tokenValid ? (
              // Token inválido
              <>
                <CardHeader className="px-0">
                  <CardTitle className="text-2xl text-red-600">Link Inválido</CardTitle>
                  <CardDescription>
                    Este link de redefinição de senha é inválido ou já expirou.
                  </CardDescription>
                </CardHeader>
                <CardContent className="px-0">
                  <div className="space-y-6">
                    <div className="flex justify-center">
                      <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
                        <XCircle className="w-10 h-10 text-red-600" />
                      </div>
                    </div>
                    <p className="text-center text-slate-600">
                      Por favor, solicite um novo link de redefinição de senha.
                    </p>
                    <div className="space-y-3">
                      <Link to="/forgot-password" className="block">
                        <Button className="w-full h-11 bg-blue-600 hover:bg-blue-700">
                          Solicitar novo link
                        </Button>
                      </Link>
                      <Link to="/login" className="block">
                        <Button variant="ghost" className="w-full h-11">
                          <ArrowLeft size={18} className="mr-2" />
                          Voltar ao login
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </>
            ) : success ? (
              // Sucesso
              <>
                <CardHeader className="px-0">
                  <CardTitle className="text-2xl text-emerald-600">Senha Redefinida!</CardTitle>
                  <CardDescription>
                    Sua senha foi alterada com sucesso.
                  </CardDescription>
                </CardHeader>
                <CardContent className="px-0">
                  <div className="space-y-6">
                    <div className="flex justify-center">
                      <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
                        <CheckCircle className="w-10 h-10 text-emerald-600" />
                      </div>
                    </div>
                    <p className="text-center text-slate-600">
                      Você já pode fazer login com sua nova senha.
                    </p>
                    <Link to="/login" className="block">
                      <Button className="w-full h-11 bg-blue-600 hover:bg-blue-700">
                        Ir para o Login
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </>
            ) : (
              // Formulário de nova senha
              <>
                <CardHeader className="px-0">
                  <CardTitle className="text-2xl">Nova Senha</CardTitle>
                  <CardDescription>
                    Digite sua nova senha abaixo.
                  </CardDescription>
                </CardHeader>
                <CardContent className="px-0">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="password">Nova Senha</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          data-testid="reset-password"
                          className="h-11 pr-10"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      <p className="text-xs text-slate-500">Mínimo de 6 caracteres</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Confirmar Nova Senha</Label>
                      <div className="relative">
                        <Input
                          id="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          value={formData.confirmPassword}
                          onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                          data-testid="reset-confirm-password"
                          className="h-11 pr-10"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full h-11 bg-blue-600 hover:bg-blue-700"
                      disabled={loading}
                      data-testid="reset-submit"
                    >
                      {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : <Lock size={18} className="mr-2" />}
                      Redefinir Senha
                    </Button>
                    <Link to="/login" className="block">
                      <Button variant="ghost" className="w-full h-11">
                        <ArrowLeft size={18} className="mr-2" />
                        Voltar ao login
                      </Button>
                    </Link>
                  </form>
                </CardContent>
              </>
            )}
          </Card>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:flex flex-1 bg-slate-900 items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-transparent" />
        <div className="relative z-10 text-center">
          <div className="w-20 h-20 rounded-2xl bg-blue-600 flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-blue-600/30">
            <span className="text-white font-bold text-3xl">R</span>
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">RPB SISTEMAS INTELIGENTES</h2>
          <p className="text-slate-400 text-lg max-w-md">
            {tokenValid ? 'Crie uma nova senha segura para sua conta.' : 'Sistema completo de vendas para sua empresa.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
